import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

const TOKEN_KEY = "esr_token";
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    client.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem(TOKEN_KEY);
    delete client.defaults.headers.common.Authorization;
  }
};
// restore token on load
const _saved = typeof localStorage !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
if (_saved) client.defaults.headers.common.Authorization = `Bearer ${_saved}`;

export const api = {
  // Auth
  register: (payload) => client.post("/auth/register", payload).then((r) => r.data),
  login: (payload) => client.post("/auth/login", payload).then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  forgotPassword: (email) => client.post("/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (token, new_password) => client.post("/auth/reset-password", { token, new_password }).then((r) => r.data),
  emailReport: (id, payload) => client.post(`/sessions/${id}/email-report`, payload).then((r) => r.data),
  listSessions: () => client.get("/sessions").then((r) => r.data),
  createSession: (payload) => client.post("/sessions", payload).then((r) => r.data),
  getSession: (id) => client.get(`/sessions/${id}`).then((r) => r.data),
  shortTitle: (id) => client.get(`/sessions/${id}/short-title`).then((r) => r.data),
  runAudit: (id) => client.post(`/sessions/${id}/audit`).then((r) => r.data),
  submitFeedback: (id, text) => client.post(`/sessions/${id}/feedback`, { text }).then((r) => r.data),
  addEvidence: (id, description, file_id) =>
    client.post(`/sessions/${id}/evidence`, { description, file_id }).then((r) => r.data),
  revise: (id, instruction, answered) => client.post(`/sessions/${id}/revise`, { instruction, answered }).then((r) => r.data),
  getVersions: (id) => client.get(`/sessions/${id}/versions`).then((r) => r.data),
  markCurrent: (id, versionId) =>
    client.post(`/sessions/${id}/versions/${versionId}/current`).then((r) => r.data),
  uploadFile: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  transcribe: (blob) => {
    const fd = new FormData();
    fd.append("file", blob, "audio.webm");
    return client.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  // Council (Differentiation Engine)
  assembleCouncil: (id) => client.post(`/sessions/${id}/council/assemble`).then((r) => r.data),
  councilFeedback: (id, text) => client.post(`/sessions/${id}/council/feedback`, { text }).then((r) => r.data),
  reviseCouncil: (id, instruction) => client.post(`/sessions/${id}/council/revise`, { instruction }).then((r) => r.data),
  getCouncilVersions: (id) => client.get(`/sessions/${id}/council/versions`).then((r) => r.data),
  markCouncilCurrent: (id, councilId) =>
    client.post(`/sessions/${id}/council/versions/${councilId}/current`).then((r) => r.data),
  createPending: (id, payload) => client.post(`/sessions/${id}/pending`, payload).then((r) => r.data),
  resolvePending: (id, pid, answer) =>
    client.post(`/sessions/${id}/pending/${pid}/resolve`, { answer }).then((r) => r.data),
  // Deliberation (Milestone 3 — collaborative construction of the Shared Problem Space)
  deliberate: (id, text) => client.post(`/sessions/${id}/deliberation`, { text }).then((r) => r.data),
  // Coordination Map (Milestone 4 — functional deliberation & coordination mapping)
  buildCoordinationMap: (id) => client.post(`/sessions/${id}/coordination/build`).then((r) => r.data),
  coordinate: (id, text) => client.post(`/sessions/${id}/coordination`, { text }).then((r) => r.data),
  // Need Structure (needs-centered engine — needs + typed tensions)
  buildNeedStructure: (id) => client.post(`/sessions/${id}/needs/build`).then((r) => r.data),
  // Areas of Need (redesign v2 — areas + area-level tensions from the problem)
  buildAreasOfNeed: (id) => client.post(`/sessions/${id}/areas/build`).then((r) => r.data),
  getAreasVersions: (id) => client.get(`/sessions/${id}/areas/versions`).then((r) => r.data),
  restoreAreasVersion: (id, versionId) => client.post(`/sessions/${id}/areas/versions/${versionId}/current`).then((r) => r.data),
  askAreas: (id, text) => client.post(`/sessions/${id}/areas/ask`, { text }).then((r) => r.data),
  commandAreas: (id, text) => client.post(`/sessions/${id}/areas/command`, { text }).then((r) => r.data),
  buildSolutionSpace: (id) => client.post(`/sessions/${id}/solutions/build`).then((r) => r.data),
  reviseSolutions: (id, text) => client.post(`/sessions/${id}/solutions/revise`, { text }).then((r) => r.data),
  saveSystemsMap: (id, map) => client.put(`/sessions/${id}/systems-map`, map).then((r) => r.data),
  switchSystemsMap: (id, systemIndex) => client.post(`/sessions/${id}/systems-map/switch`, { system_index: systemIndex }).then((r) => r.data),
  explainRelationship: (id, body) => client.post(`/sessions/${id}/systems-map/explain`, body).then((r) => r.data),
  buildPlan: (id) => client.post(`/sessions/${id}/plan/build`).then((r) => r.data),
  // Integrated Solutions (Milestone 5 — generation & refinement of integrated solutions)
  generateSolutions: (id) => client.post(`/sessions/${id}/solutions/generate`).then((r) => r.data),
  solutionFeedback: (id, text) => client.post(`/sessions/${id}/solutions/feedback`, { text }).then((r) => r.data),
  // Admin (admin role only)
  adminStats: () => client.get("/admin/stats").then((r) => r.data),
  adminUsers: () => client.get("/admin/users").then((r) => r.data),
  adminDeleteUser: (id) => client.delete(`/admin/users/${id}`).then((r) => r.data),
  adminChallenges: () => client.get("/admin/challenges").then((r) => r.data),
  adminDeleteChallenge: (id) => client.delete(`/admin/challenges/${id}`).then((r) => r.data),
  adminEmails: () => client.get("/admin/emails").then((r) => r.data),
  adminFeedback: () => client.get("/admin/feedback").then((r) => r.data),
  submitAppFeedback: (payload) => client.post("/feedback", payload).then((r) => r.data),
};

// Public sample walkthrough: id "demo" reads a fixed example and blocks changes.
export const DEMO_ID = "demo";
let demoData;
const demoSession = async () => (demoData ||= (await client.get("/demo/session")).data);
const demoOverrides = {
  getSession: demoSession,
  getVersions: async () => [(await demoSession()).representation],
  shortTitle: async () => ({ title: (await demoSession()).session.short_title }),
  getAreasVersions: async () => [],
  saveSystemsMap: async () => ({}),
  explainRelationship: (_id, body) => client.post("/demo/explain", body).then((r) => r.data),
};
const demoBlocked = () => {
  const e = new Error("This is a sample walkthrough — create a free account to try this on your own challenge.");
  e.response = { data: { detail: e.message } };
  return Promise.reject(e);
};
Object.keys(api).forEach((k) => {
  const fn = api[k];
  api[k] = (...args) => (args[0] === DEMO_ID ? (demoOverrides[k] ? demoOverrides[k](...args) : demoBlocked()) : fn(...args));
});

export default api;
